import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResponseTrackingDashboard = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  // Mock platform response data
  const [platformResponses] = useState([
    {
      platform: 'Facebook',
      logo: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=32&h=32&fit=crop',
      totalRequests: 45,
      processed: 38,
      pending: 7,
      responseTime: '2.3h',
      compliance: 84,
      actions: {
        contentRemoved: 15,
        contentLabeled: 18,
        accountsSuspended: 3,
        reachReduced: 12
      },
      trend: 'up'
    },
    {
      platform: 'Twitter',
      logo: 'https://images.unsplash.com/photo-1611605698335-8b1569810432?w=32&h=32&fit=crop',
      totalRequests: 32,
      processed: 29,
      pending: 3,
      responseTime: '1.8h',
      compliance: 91,
      actions: {
        contentRemoved: 12,
        contentLabeled: 14,
        accountsSuspended: 2,
        reachReduced: 8
      },
      trend: 'up'
    },
    {
      platform: 'TikTok',
      logo: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=32&h=32&fit=crop',
      totalRequests: 28,
      processed: 21,
      pending: 7,
      responseTime: '4.1h',
      compliance: 75,
      actions: {
        contentRemoved: 8,
        contentLabeled: 9,
        accountsSuspended: 1,
        reachReduced: 6
      },
      trend: 'down'
    },
    {
      platform: 'YouTube',
      logo: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=32&h=32&fit=crop',
      totalRequests: 19,
      processed: 17,
      pending: 2,
      responseTime: '3.2h',
      compliance: 89,
      actions: {
        contentRemoved: 7,
        contentLabeled: 8,
        accountsSuspended: 1,
        reachReduced: 4
      },
      trend: 'stable'
    },
    {
      platform: 'Instagram',
      logo: 'https://images.unsplash.com/photo-1611262588024-d12430b98920?w=32&h=32&fit=crop',
      totalRequests: 23,
      processed: 20,
      pending: 3,
      responseTime: '2.7h',
      compliance: 87,
      actions: {
        contentRemoved: 9,
        contentLabeled: 10,
        accountsSuspended: 1,
        reachReduced: 5
      },
      trend: 'up'
    }
  ]);

  // Mock recent actions
  const [recentActions] = useState([
    {
      id: 1,
      platform: 'Facebook',
      action: 'Content Removed',
      contentId: 'FB-2024-0921-001',
      requestTime: new Date(Date.now() - 1800000),
      responseTime: new Date(Date.now() - 900000),
      status: 'completed',
      reason: 'Coordinated Inauthentic Behavior',
      impact: 'High - 45K reach prevented'
    },
    {
      id: 2,
      platform: 'Twitter',
      action: 'Content Labeled',
      contentId: 'TW-2024-0921-002',
      requestTime: new Date(Date.now() - 2400000),
      responseTime: new Date(Date.now() - 1200000),
      status: 'completed',
      reason: 'Misleading Information',
      impact: 'Medium - Warning label applied'
    },
    {
      id: 3,
      platform: 'TikTok',
      action: 'Reach Reduced',
      contentId: 'TT-2024-0921-003',
      requestTime: new Date(Date.now() - 3600000),
      responseTime: null,
      status: 'pending',
      reason: 'Harmful Misinformation',
      impact: 'Pending assessment'
    },
    {
      id: 4,
      platform: 'YouTube',
      action: 'Account Suspended',
      contentId: 'YT-2024-0921-004',
      requestTime: new Date(Date.now() - 4800000),
      responseTime: new Date(Date.now() - 2400000),
      status: 'completed',
      reason: 'Repeat Policy Violations',
      impact: 'High - Channel terminated'
    }
  ]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return 'TrendingUp';
      case 'down': return 'TrendingDown';
      case 'stable': return 'Minus';
      default: return 'Minus';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      case 'stable': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'failed': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Pending';
    const minutes = Math.floor((Date.now() - date?.getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const calculateResponseTime = (requestTime, responseTime) => {
    if (!responseTime) return 'Pending';
    const minutes = Math.floor((responseTime?.getTime() - requestTime?.getTime()) / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const filteredPlatforms = selectedPlatform === 'all' 
    ? platformResponses 
    : platformResponses?.filter(p => p?.platform?.toLowerCase() === selectedPlatform);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Icon name="BarChart3" size={20} className="text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Platform Response Tracking</h2>
            <p className="text-sm text-gray-500">VLOP compliance monitoring</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <select 
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e?.target?.value)}
            className="text-sm border border-gray-300 rounded px-3 py-1.5"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          
          <select 
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e?.target?.value)}
            className="text-sm border border-gray-300 rounded px-3 py-1.5"
          >
            <option value="all">All Platforms</option>
            <option value="facebook">Facebook</option>
            <option value="twitter">Twitter</option>
            <option value="tiktok">TikTok</option>
            <option value="youtube">YouTube</option>
            <option value="instagram">Instagram</option>
          </select>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export Report
          </Button>
        </div>
      </div>
      {/* Platform Overview Cards */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {filteredPlatforms?.map((platform) => (
            <div key={platform?.platform} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <img
                    src={platform?.logo}
                    alt={platform?.platform}
                    className="w-6 h-6 rounded"
                    onError={(e) => {
                      e.target.src = '/assets/images/no_image.png';
                    }}
                  />
                  <span className="text-sm font-medium text-gray-900">{platform?.platform}</span>
                </div>
                <Icon 
                  name={getTrendIcon(platform?.trend)} 
                  size={16} 
                  className={getTrendColor(platform?.trend)} 
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Requests</span>
                  <span className="font-medium text-gray-900">{platform?.totalRequests}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Processed</span>
                  <span className="font-medium text-green-600">{platform?.processed}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Pending</span>
                  <span className="font-medium text-yellow-600">{platform?.pending}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Avg Response</span>
                  <span className="font-medium text-gray-900">{platform?.responseTime}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Compliance</span>
                  <span className={`font-medium ${
                    platform?.compliance >= 90 ? 'text-green-600' :
                    platform?.compliance >= 80 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {platform?.compliance}%
                  </span>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(platform?.processed / platform?.totalRequests) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Progress</span>
                  <span>{Math.round((platform?.processed / platform?.totalRequests) * 100)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Action Breakdown */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Action Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {['contentRemoved', 'contentLabeled', 'accountsSuspended', 'reachReduced']?.map((actionType) => {
            const total = filteredPlatforms?.reduce((sum, platform) => sum + platform?.actions?.[actionType], 0);
            const actionLabels = {
              contentRemoved: 'Content Removed',
              contentLabeled: 'Content Labeled',
              accountsSuspended: 'Accounts Suspended',
              reachReduced: 'Reach Reduced'
            };
            
            return (
              <div key={actionType} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 mb-1">{total}</div>
                <div className="text-sm text-gray-600">{actionLabels?.[actionType]}</div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Recent Actions */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Recent Actions</h3>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => window.location?.reload()}
          >
            Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">Platform</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Action</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Content ID</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Response Time</th>
                <th className="text-left py-3 px-4 font-medium text-gray-900">Impact</th>
              </tr>
            </thead>
            <tbody>
              {recentActions?.map((action) => (
                <tr key={action?.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">
                          {action?.platform?.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{action?.platform}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-900">{action?.action}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-mono text-gray-600">{action?.contentId}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(action?.status)}`}>
                      {action?.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-900">
                      {calculateResponseTime(action?.requestTime, action?.responseTime)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-600">{action?.impact}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResponseTrackingDashboard;